'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMemo } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import {
  configurationQueries,
  type RejectionReason
} from '@/features/configurations';
import { useRejectSessionMutation } from '../services/qr-review.mutations';
import type { QrReviewRejectStage } from '../types';

const TARGET_STAGES: readonly QrReviewRejectStage[] = [
  'handover_scanning',
  'photo_scanning'
];

interface QrReviewRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: number;
  onSuccess?: () => void;
}

export function QrReviewRejectDialog({
  open,
  onOpenChange,
  batchId,
  onSuccess
}: QrReviewRejectDialogProps) {
  const t = useTranslations('qrReview');
  const tv = useTranslations('validation');
  const locale = useLocale() as 'en' | 'ar';

  const rejectMutation = useRejectSessionMutation();
  const { data: configData } = useQuery(configurationQueries.all());
  const configSettings = (configData as any)?.data ?? configData ?? {};
  const perStageReasons: Record<string, RejectionReason[]> =
    configSettings.rejection_reasons &&
    !Array.isArray(configSettings.rejection_reasons)
      ? (configSettings.rejection_reasons as Record<string, RejectionReason[]>)
      : {};

  const schema = useMemo(
    () =>
      z.object({
        target_stage: z.enum(['handover_scanning', 'photo_scanning'], {
          message: tv('targetStageRequired')
        }),
        rejection_reason_code: z
          .string()
          .min(1, { message: tv('reasonRequired') }),
        notes: z.string().max(1000).optional()
      }),
    [tv]
  );

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      target_stage: 'photo_scanning',
      rejection_reason_code: '',
      notes: ''
    }
  });

  const selectedStage = form.watch('target_stage');
  const dynamicReasons = useMemo(
    () =>
      (perStageReasons[selectedStage] ?? [])
        .filter((r) => (r.status ?? 'active') !== 'inactive')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStage, configSettings.rejection_reasons]
  );

  const stageLabels: Record<QrReviewRejectStage, string> = {
    handover_scanning: t('stageHandoverScanning'),
    photo_scanning: t('stagePhotoScanning')
  };

  async function onSubmit(values: FormValues) {
    await rejectMutation.mutateAsync({
      batchId,
      target_stage: values.target_stage,
      rejection_reason_code: values.rejection_reason_code,
      notes: values.notes?.trim() ? values.notes.trim() : undefined
    });

    form.reset();
    onOpenChange(false);
    onSuccess?.();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('rejectTitle')}</DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-5'
        >
          <FormField
            control={form.control}
            name='target_stage'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('targetStage')}</FormLabel>
                <Select
                  onValueChange={(val) => {
                    field.onChange(val);
                    form.setValue('rejection_reason_code', '');
                  }}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TARGET_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {stageLabels[stage]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='rejection_reason_code'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('rejectionReason')}</FormLabel>
                <FormControl>
                  {dynamicReasons.length === 0 ? (
                    <p className='text-muted-foreground text-sm'>
                      {t('rejectNoReasonsConfigured')}
                    </p>
                  ) : (
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className='space-y-2'
                    >
                      {dynamicReasons.map((reason) => (
                        <div
                          key={reason.code}
                          className='flex items-center space-x-2'
                        >
                          <RadioGroupItem
                            value={reason.code}
                            id={`qr-reason-${reason.code}`}
                          />
                          <Label
                            htmlFor={`qr-reason-${reason.code}`}
                            className='cursor-pointer'
                          >
                            {reason[locale] ?? reason.en}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='notes'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('additionalNotes')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t('additionalNotesPlaceholder')}
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              type='submit'
              variant='destructive'
              disabled={rejectMutation.isPending || dynamicReasons.length === 0}
            >
              {t('confirmReject')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
